// src/components/layout/Sidebar.jsx
// STATUS: active | UPDATED: 2026-08-02
// LOG:
//   2026-08-02  Header standardized; Phase 1 checkpoint committed (e765830).
//   2026-08-01  Coach added to daily items; Training label for /courses/
// Left sidebar nav. Logo at top, four daily items: Today, Courses, Calendar,
// Team. Insights folded into the dashboard AI coach. Clean black active state.
//
// Admin (2026-07-23): rendered only when the user has the admin role, and set
// below a divider with its own small label. It is a different kind of
// destination than the four daily items, so it reads as a separate group
// rather than a fifth tab. This mirrors the mobile More sheet, where Admin
// also sits below a divider.
//
// Hiding the item is cosmetic. Real protection lives in ProtectedRoute and in
// Supabase RLS policies.
//
// Settings and Sign out intentionally live in the Header avatar menu, not
// here, so there is exactly one place to find account actions on desktop.

import { NavLink, useLocation } from 'react-router-dom'
import { Box, VStack, HStack, Text, Icon } from '@chakra-ui/react'
import { LayoutDashboard, Map, Sparkles, Calendar, Users, ShieldCheck, TrendingUp, Store, ClipboardCheck, FileText, BarChart3, MessageSquare, Sun } from 'lucide-react'
import { useAuth } from '../../hooks/useAuth'
import { navFor, operatorNavFor } from '../../lib/nav'
import DealershipLogo from '../ui/DealershipLogo'
import Logo from '../ui/Logo.jsx'

// Icons live here rather than in lib/nav.js so that module stays free of React
// and can be reasoned about, and tested, as plain data.
const ICONS = { LayoutDashboard, Map, Sparkles, Calendar, Users, ShieldCheck, TrendingUp, Store, ClipboardCheck, FileText, BarChart3, MessageSquare, Sun }

function NavItem({ to, label, icon: IconComponent }) {
  const location = useLocation()
  const isActive = location.pathname.startsWith(to)
  return (
    <NavLink to={to} style={{ textDecoration: 'none', width: '100%' }}>
      <HStack
        px={4}
        py={2.5}
        borderRadius="md"
        bg={isActive ? 'ink' : 'transparent'}
        color={isActive ? 'onInk' : 'inkMuted'}
        _hover={{ bg: isActive ? 'ink' : 'surface', color: isActive ? 'onInk' : 'ink' }}
        transition="all 150ms"
        spacing={3}
      >
        <Icon as={IconComponent} boxSize={4} />
        <Text fontSize="sm" fontWeight={isActive ? 600 : 500}>
          {label}
        </Text>
      </HStack>
    </NavLink>
  )
}

export default function Sidebar() {
  const { role, dealership, profile } = useAuth()
  // `is_method_author` is one person. He shares the admin role with Tyler and
  // not the job, so the menu differs even though the permissions do not.
  const author = profile?.is_method_author === true
  const items = navFor(role, { author })
  const operator = operatorNavFor(role, { author })

  return (
    <Box
      as="aside"
      w={{ base: 'full', lg: '240px' }}
      minH="100vh"
      bg="card"
      borderRight="1px solid"
      borderColor="line"
      px={4}
      py={6}
      position={{ base: 'relative', lg: 'sticky' }}
      top={0}
    >
      <VStack align="stretch" spacing={8}>
        <Box px={2}>
          <Logo w="150px" h="auto" />
          {/* Their mark under ours, the way a dealership's own sign sits under
              the manufacturer's on a real building. Renders nothing when they
              have not given us one, which is most of them. */}
          {dealership?.logo_path && (
            <Box mt={3} pt={3} borderTop="1px solid" borderColor="line">
              <DealershipLogo dealership={dealership} maxH="26px" />
            </Box>
          )}
        </Box>

        <VStack align="stretch" spacing={1}>
          {items.map((item) => (
            <NavItem key={item.key} to={item.to} label={item.label} icon={ICONS[item.icon]} />
          ))}
        </VStack>

        {/* Level 00 only. A different job, not a deeper version of the same one,
            which is why it sits under its own heading rather than in the list. */}
        {operator.length > 0 && (
          <VStack align="stretch" spacing={1}>
            <Box h="1px" bg="line" mb={2} aria-hidden="true" />
            <Text
              px={4}
              pb={1}
              fontSize="10px"
              fontWeight={600}
              letterSpacing="0.12em"
              textTransform="uppercase"
              color="inkDim"
            >
              Manage
            </Text>
            {operator.map((item) => (
              <NavItem key={item.key} to={item.to} label={item.label} icon={ICONS[item.icon]} />
            ))}
          </VStack>
        )}
      </VStack>
    </Box>
  )
}